import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CacheEntry {
  id: string;
  user_id: string;
  target_user_id: string;
  compatibility_score: number;
  ai_summary: string | null;
  common_points: string[] | null;
  differences: string[] | null;
  ai_model: string | null;
  created_at: string | null;
  updated_at: string | null;
  user1_username?: string;
  user2_username?: string;
  user1_photo?: string;
  user2_photo?: string;
}

export interface CacheStats {
  total: number;
  byModel: Record<string, number>;
  mostRecent: string | null;
}

export function useCompatibilityCache() {
  const [entries, setEntries] = useState<CacheEntry[]>([]);
  const [stats, setStats] = useState<CacheStats>({ total: 0, byModel: {}, mostRecent: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('profile_compatibility')
        .select(`
          *,
          user1:profiles!profile_compatibility_user_id_fkey(username, main_photo_url),
          user2:profiles!profile_compatibility_target_user_id_fkey(username, main_photo_url)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedEntries: CacheEntry[] = (data || []).map((entry: any) => ({
        ...entry,
        user1_username: entry.user1?.username,
        user2_username: entry.user2?.username,
        user1_photo: entry.user1?.main_photo_url,
        user2_photo: entry.user2?.main_photo_url,
      }));

      setEntries(formattedEntries);

      // Calculate stats
      const byModel: Record<string, number> = {};
      formattedEntries.forEach(e => {
        const model = e.ai_model || 'unknown';
        byModel[model] = (byModel[model] || 0) + 1;
      });

      setStats({
        total: formattedEntries.length,
        byModel,
        mostRecent: formattedEntries[0]?.created_at || null,
      });
    } catch (error) {
      console.error('Error fetching cache entries:', error);
      toast.error('Erro ao carregar cache');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteEntry = useCallback(async (id: string) => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase
        .from('profile_compatibility')
        .delete()
        .eq('id', id)
        .select();

      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Nenhum registro deletado - verifique permissões RLS');
      }

      console.log('[CompatibilityCache] Deleted entry:', id, 'rows affected:', data.length);
      setEntries(prev => prev.filter(e => e.id !== id));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
      toast.success('Entrada removida');
    } catch (error) {
      console.error('[CompatibilityCache] Error deleting cache entry:', error);
      toast.error('Erro ao remover entrada: ' + (error as Error).message);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const clearAllCache = useCallback(async () => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase
        .from('profile_compatibility')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000')
        .select();

      if (error) throw error;

      console.log('[CompatibilityCache] Cleared all cache, rows deleted:', data?.length || 0);
      setEntries([]);
      setStats({ total: 0, byModel: {}, mostRecent: null });
      toast.success(`Cache limpo: ${data?.length || 0} entradas removidas`);
    } catch (error) {
      console.error('[CompatibilityCache] Error clearing cache:', error);
      toast.error('Erro ao limpar cache: ' + (error as Error).message);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    stats,
    isLoading,
    isDeleting,
    fetchEntries,
    deleteEntry,
    clearAllCache,
  };
}
